import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { History, Undo2, X, AlertTriangle, Check } from 'lucide-react'
import { api } from '../lib/api'
import { formatDateTime } from '../lib/utils'
import { useAuth } from '../context/AuthContext'

// Product import history + one-click undo (shipped 2026-09-20 after
// the 2026-09-17 CSV overwrote name/description/image on a handful of
// SKUs). Every /admin/products/csv/import writes a snapshot row; this
// page lists them + surfaces a diff + Undo button per import.

interface ImportRow {
  id: string
  admin_id: string
  admin_name: string | null
  applied_at: string
  row_count: number
  created_count: number
  updated_count: number
  csv_size_bytes: number
  undone_at: string | null
  undone_by_admin_id: string | null
  undo_result: null | { restored: string[]; deleted: string[]; skipped_conflict: string[]; skipped_missing: string[] }
}

interface ImportDetail {
  import: ImportRow & { csv_text: string }
  updated_rows: Array<{ id: string; before: Record<string, unknown> | null; current: Record<string, unknown> | null }>
  created_rows: Array<{ id: string; current: Record<string, unknown> | null }>
}

const DIFF_COLS = [
  'name_en', 'name_ar', 'description_en', 'description_ar',
  'category_id', 'brand', 'type', 'volume_ml', 'abv',
  'price', 'cost_fils', 'compare_price', 'stock',
  'image_key', 'active', 'variant_group_id',
] as const

export default function ProductImports() {
  const { can } = useAuth()
  const canManage = can(['super_admin', 'operations'])
  const qc = useQueryClient()
  const [openId, setOpenId] = useState<string | null>(null)

  const { data, isLoading } = useQuery<{ imports: ImportRow[] }>({
    queryKey: ['product-imports'],
    queryFn: () => api.get('/product-imports').then(r => r.data),
  })

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-white flex items-center gap-2">
          <History size={20} className="text-[#c41e3a]" />
          Product import history
        </h1>
        <p className="text-sm text-gray-500 mt-1 max-w-3xl">
          Every CSV import is snapshotted before it&apos;s applied, so a bad import can be reversed with one click. Undo restores each row&apos;s pre-import state; rows that were edited by hand after the import are skipped and shown separately so you can inspect them.
        </p>
      </div>

      <div className="bg-[#1a1a1a] rounded-xl border border-white/10 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (data?.imports ?? []).length === 0 ? (
          <div className="p-8 text-center text-sm text-gray-500">No imports yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-xs uppercase tracking-wide text-gray-500">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Applied at</th>
                <th className="text-left px-4 py-3 font-medium">By</th>
                <th className="text-right px-4 py-3 font-medium">Rows</th>
                <th className="text-right px-4 py-3 font-medium">Created</th>
                <th className="text-right px-4 py-3 font-medium">Updated</th>
                <th className="text-right px-4 py-3 font-medium">Size</th>
                <th className="text-left px-4 py-3 font-medium">Status</th>
                <th className="text-right px-4 py-3 font-medium"></th>
              </tr>
            </thead>
            <tbody>
              {data!.imports.map(r => (
                <tr key={r.id} className="border-t border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 text-gray-300 font-mono text-xs">{formatDateTime(r.applied_at)}</td>
                  <td className="px-4 py-3 text-gray-300">{r.admin_name ?? '—'}</td>
                  <td className="px-4 py-3 text-right text-white font-semibold">{r.row_count}</td>
                  <td className="px-4 py-3 text-right text-green-400">{r.created_count}</td>
                  <td className="px-4 py-3 text-right text-amber-400">{r.updated_count}</td>
                  <td className="px-4 py-3 text-right text-gray-400 text-xs">{(r.csv_size_bytes / 1024).toFixed(1)} KB</td>
                  <td className="px-4 py-3">
                    {r.undone_at ? (
                      <span className="text-xs text-gray-400 flex items-center gap-1">
                        <Check size={12} /> Undone {formatDateTime(r.undone_at)}
                      </span>
                    ) : (
                      <span className="text-xs text-green-400">Active</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => setOpenId(r.id)}
                      className="text-[#c41e3a] hover:underline text-xs font-semibold"
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {openId && (
        <ImportDetailModal
          id={openId}
          canManage={canManage}
          onClose={() => setOpenId(null)}
          onUndone={() => qc.invalidateQueries({ queryKey: ['product-imports'] })}
        />
      )}
    </div>
  )
}

function ImportDetailModal({ id, canManage, onClose, onUndone }: {
  id: string
  canManage: boolean
  onClose: () => void
  onUndone: () => void
}) {
  const { data, isLoading } = useQuery<ImportDetail>({
    queryKey: ['product-import', id],
    queryFn: () => api.get(`/product-imports/${id}`).then(r => r.data),
  })

  const [confirming, setConfirming] = useState(false)
  const [undoResult, setUndoResult] = useState<null | { restored: string[]; deleted: string[]; skipped_conflict: string[]; skipped_missing: string[] }>(null)

  const undo = useMutation({
    mutationFn: async () => api.post(`/product-imports/${id}/undo`, {}).then(r => r.data),
    onSuccess: (result) => { setUndoResult(result); onUndone() },
  })

  const imp = data?.import

  return (
    <div className="fixed inset-0 z-40 bg-black/70 flex items-start justify-center overflow-y-auto p-6">
      <div className="bg-[#1a1a1a] w-full max-w-5xl rounded-xl border border-white/10 my-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-white/10">
          <h2 className="text-white text-lg font-semibold flex items-center gap-2">
            <History size={16} className="text-[#c41e3a]" />
            Import detail
          </h2>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={18} /></button>
        </div>

        {isLoading || !imp ? (
          <div className="p-8 text-center text-sm text-gray-500">Loading…</div>
        ) : (
          <div className="p-5 space-y-5 text-sm">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-xs">
              <div><div className="text-gray-500 uppercase tracking-wide">Applied</div><div className="text-white mt-0.5">{formatDateTime(imp.applied_at)}</div></div>
              <div><div className="text-gray-500 uppercase tracking-wide">By</div><div className="text-white mt-0.5">{imp.admin_name ?? '—'}</div></div>
              <div><div className="text-gray-500 uppercase tracking-wide">Rows</div><div className="text-white mt-0.5">{imp.row_count} <span className="text-gray-500">({imp.created_count}+{imp.updated_count})</span></div></div>
              <div><div className="text-gray-500 uppercase tracking-wide">Status</div><div className="mt-0.5">{imp.undone_at ? <span className="text-gray-400">Undone {formatDateTime(imp.undone_at)}</span> : <span className="text-green-400">Active</span>}</div></div>
            </div>

            {imp.undo_result && (
              <div className="rounded-lg bg-white/[0.03] border border-white/10 p-3 text-xs text-gray-300">
                <div className="font-semibold text-white mb-1">Undo result</div>
                <div>Restored: {imp.undo_result.restored.length}</div>
                <div>Deleted (created rows): {imp.undo_result.deleted.length}</div>
                {imp.undo_result.skipped_conflict.length > 0 && <div className="text-amber-400">Skipped (edited after import): {imp.undo_result.skipped_conflict.length}</div>}
                {imp.undo_result.skipped_missing.length > 0 && <div className="text-gray-500">Skipped (row deleted since): {imp.undo_result.skipped_missing.length}</div>}
              </div>
            )}

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Updated rows ({data.updated_rows.length}) — cell-level diff</div>
              <div className="max-h-96 overflow-y-auto border border-white/10 rounded-lg">
                <table className="w-full text-xs">
                  <thead className="sticky top-0 bg-black/60 text-gray-500">
                    <tr>
                      <th className="text-left px-2 py-2">id</th>
                      <th className="text-left px-2 py-2">column</th>
                      <th className="text-left px-2 py-2">before</th>
                      <th className="text-left px-2 py-2">current</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.updated_rows.flatMap(row => {
                      if (!row.before) return []
                      return DIFF_COLS.filter(c => (row.before as Record<string, unknown>)[c] !== (row.current as Record<string, unknown> | null)?.[c])
                        .map(c => (
                          <tr key={`${row.id}-${c}`} className="border-t border-white/5">
                            <td className="px-2 py-1 font-mono text-gray-500">{row.id.slice(0, 8)}..</td>
                            <td className="px-2 py-1 text-white/80">{c}</td>
                            <td className="px-2 py-1 text-gray-400 max-w-xs truncate">{String((row.before as Record<string, unknown>)[c] ?? '(null)')}</td>
                            <td className="px-2 py-1 text-white/90 max-w-xs truncate">{String((row.current as Record<string, unknown> | null)?.[c] ?? '(null)')}</td>
                          </tr>
                        ))
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">Created rows ({data.created_rows.length})</div>
              <div className="text-xs text-gray-400">
                {data.created_rows.length === 0 ? '—' : data.created_rows.map(r => (r.current as Record<string, unknown> | null)?.name_en as string | undefined ?? r.id).join(', ')}
              </div>
            </div>

            {canManage && !imp.undone_at && (
              <div className="pt-4 border-t border-white/10">
                {!confirming && !undoResult ? (
                  <button
                    onClick={() => setConfirming(true)}
                    className="flex items-center gap-2 px-4 py-2 rounded bg-[#c41e3a] hover:bg-[#a01830] text-white text-sm font-semibold"
                  >
                    <Undo2 size={14} /> Undo this import
                  </button>
                ) : undoResult ? (
                  <div className="text-sm text-green-400">Done. Restored {undoResult.restored.length}, deleted {undoResult.deleted.length}{undoResult.skipped_conflict.length > 0 ? `, skipped ${undoResult.skipped_conflict.length} due to post-import edits` : ''}.</div>
                ) : (
                  <div className="flex items-start gap-3 bg-amber-500/10 border border-amber-500/30 rounded-lg p-3">
                    <AlertTriangle size={16} className="text-amber-400 mt-0.5" />
                    <div className="flex-1 text-xs text-amber-200">
                      <div className="font-semibold mb-1">Undo this import?</div>
                      <div>Restores each row&apos;s pre-import state. Rows edited after the import are skipped. Cannot be redone from here — you&apos;d re-run the same CSV.</div>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => setConfirming(false)} className="text-xs text-gray-400 hover:text-white">Cancel</button>
                      <button
                        disabled={undo.isPending}
                        onClick={() => undo.mutate()}
                        className="text-xs bg-amber-500 hover:bg-amber-400 text-black font-semibold px-3 py-1 rounded"
                      >
                        {undo.isPending ? 'Undoing…' : 'Yes, undo'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
